import { FC, useCallback, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { useShallowCompareEffect } from 'react-use'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { CheckmarkFilledIcon } from '@getgo/chameleon-icons/react'
import { Menu, MenuItem, MenuSection } from '@getgo/chameleon-web-react-wrapper'
import { Box, makeStyles } from '@material-ui/core'
import { writeStorage } from '@rehooks/local-storage'
import clsx from 'clsx'
import mixpanel from 'mixpanel-browser'

import { useAccountContext } from 'components/contexts/AccountContext'
import SearchField from 'components/SearchField'
import config from 'config'
import { Account } from 'types/Account'
import { mixpanelEventNames } from 'utils'
import { MINIMUM_ACCOUNT_NUMBER_TO_SHOW_SEARCH_FIELD } from '../../utils/constants'

const useStyles = makeStyles(() => ({
  box: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: 284,
  },
  menuSection: {
    margin: '0.25rem 1rem',
    display: 'flex',
    color: tokens.typeColorSecondary,
    font: tokens.captionMediumStrong,
  },
  menuSelected: {
    font: tokens.bodySmallStrong,
  },
}))

interface DisplayMenuSections {
  search: boolean
  accounts: boolean
}

const defaultDisplayMenuSections: DisplayMenuSections = {
  search: false,
  accounts: false,
}

interface AccountMenuItem {
  account: Account
  className: string
  selected: boolean
  onClick: (account: Account) => void
}

const AccountMenuItem: FC<AccountMenuItem> = ({ account, onClick, className, selected = false, ...props }) => {
  const classes = useStyles()
  return (
    <MenuItem
      className={clsx([selected && classes.menuSelected])}
      key={account.key}
      onClick={() => onClick(account)}
      checked={selected}
      leadingIcon={selected ? <CheckmarkFilledIcon /> : <Box width={20} height={32} />}
      role="menuitem"
      {...props}
    >
      <Box title={account.name} className={className}>
        {account.name}
      </Box>
    </MenuItem>
  )
}

interface AccountMenuSimpleProps {
  accounts: Account[]
  closePopover: () => void
}

const AccountMenu: FC<AccountMenuSimpleProps> = ({ accounts, closePopover }) => {
  const classes = useStyles()
  const t = useTranslate()
  const { setSelectedAccountKey, accountKey: currentAccountKey } = useAccountContext()

  const [filter, setFilter] = useState<string>('')
  const [displayMenuSections, setDisplayMenuSections] = useState<DisplayMenuSections>(defaultDisplayMenuSections)
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([])

  const selectAccount = useCallback(
    (account: Account) => {
      if (currentAccountKey !== account.key) {
        setSelectedAccountKey(account.key)
        setFilter('')
        writeStorage(config.SELECTED_ACCOUNT_KEY, { key: account.key })
        mixpanel.track(mixpanelEventNames.ACCOUNT_SWITCHER, {
          Action: `Selected an account`,
          ...account,
        })
      }

      closePopover()
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePopover, currentAccountKey],
  )

  useShallowCompareEffect(() => {
    const accountsFiltered = accounts.filter((account) => account.name.toLowerCase().includes(filter.toLowerCase()))

    setFilteredAccounts(accountsFiltered)

    setDisplayMenuSections({
      search: accounts.length >= MINIMUM_ACCOUNT_NUMBER_TO_SHOW_SEARCH_FIELD,
      accounts: !!accountsFiltered.length,
    })
  }, [filter, accounts])

  return (
    <>
      {displayMenuSections.search && (
        <SearchField
          data-testid="account-menu-simple-search-input"
          placeholder={t('Search...')}
          style={{ padding: 8 }}
          fullwidth={true}
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value || '')
          }}
          onRemove={() => setFilter('')}
        />
      )}
      <Menu data-testid="account-menu" style={{ maxHeight: `calc(100vh - 120px)`, overflow: 'auto' }}>
        {displayMenuSections.accounts ? (
          <>
            <MenuSection className={classes.menuSection}>{t('My Accounts')}</MenuSection>
            {filteredAccounts.map((account) => (
              <AccountMenuItem
                selected={account.key === currentAccountKey}
                className={clsx([classes.box])}
                key={account.key}
                account={account}
                onClick={selectAccount}
              />
            ))}
          </>
        ) : (
          <MenuItem data-testid="account-menu-simple-no-account-found">{t('No account found')}</MenuItem>
        )}
      </Menu>
    </>
  )
}

export default AccountMenu
